import { useState } from 'react'
import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'

import DialogShell from '../common/DialogShell'
import FormSection, { FieldPair } from '../common/FormSection'
import { todayLocalYMD } from '../../utils/dateFormat'
import { SIGNAL_TYPES, SIGNAL_TYPE_LABELS } from '../../state/jobOpportunities'
import type { SignalType } from '../../state/jobOpportunities'

interface LogHiddenOpportunityDialogProps {
  open: boolean
  onClose: () => void
  clientId: string
  clientName: string
  onLog: (data: {
    clientId: string
    clientName: string
    company: string
    jobTitle: string
    hiddenOpportunity: true
    signalType: SignalType
    signalText: string
    signalObservedDate: string
    signalSource?: string
    vacatedBy?: string
  }) => Promise<void>
}

const EMPTY = {
  company: '',
  jobTitle: '',
  signalType: 'leadership_change' as SignalType,
  signalText: '',
  vacatedBy: '',
  signalSource: '',
}

/**
 * MWA-304 — logs a role staff believe is opening from something that happened
 * publicly, before any posting exists. Separate from AddJobDialog rather than a
 * mode inside it: that dialog's middle section is entirely about a pipeline
 * status and an applied date, and a hidden opportunity has neither.
 */
export default function LogHiddenOpportunityDialog({
  open,
  onClose,
  clientId,
  clientName,
  onLog,
}: LogHiddenOpportunityDialogProps) {
  const [form, setForm] = useState({ ...EMPTY, signalObservedDate: todayLocalYMD() })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const change = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const validate = () => {
    const next: Record<string, string> = {}
    if (!form.company.trim()) next.company = 'Company is required'
    if (!form.jobTitle.trim()) next.jobTitle = 'Role is required'
    if (!form.signalText.trim()) next.signalText = 'Say what happened — it is the point of the record'
    if (!form.signalObservedDate) next.signalObservedDate = 'Observed date is required'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const reset = () => {
    setForm({ ...EMPTY, signalObservedDate: todayLocalYMD() })
    setErrors({})
    setSubmitError(null)
  }

  const handleSubmit = async () => {
    if (!validate()) return
    setSubmitting(true)
    setSubmitError(null)
    try {
      await onLog({
        clientId,
        clientName,
        company: form.company.trim(),
        jobTitle: form.jobTitle.trim(),
        hiddenOpportunity: true,
        signalType: form.signalType,
        signalText: form.signalText.trim(),
        signalObservedDate: form.signalObservedDate,
        signalSource: form.signalSource.trim() || undefined,
        vacatedBy: form.vacatedBy.trim() || undefined,
      })
      reset()
      onClose()
    } catch (error) {
      // Kept on screen, and the form is left intact. MWA-297 shipped a dialog
      // that cleared itself on a rejected write exactly as it did on a
      // successful one, so a refused save looked saved and the typing was gone.
      const message = error instanceof Error ? error.message : 'Unknown error'
      setSubmitError(
        `Could not log this opportunity (${message}). Your entry is still here — try again.`
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    reset()
    onClose()
  }


  return (
    <DialogShell
      open={open}
      onClose={handleClose}
      width={760}
      disableDismiss={submitting}
      title="Log a Hidden Opportunity"
      subtitle={
        <>
          For <strong>{clientName || '(unnamed client)'}</strong>. The client sees this as a
          read-only notice.
        </>
      }
      footer={
        <>
          <Button onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Logging...' : 'Log Opportunity'}
          </Button>
        </>
      }
    >
      {submitError && (
        <Alert severity="error" variant="outlined" onClose={() => setSubmitError(null)} sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      <FormSection title="The role" first>
        <FieldPair>
          <TextField
            label="Company"
            value={form.company}
            onChange={(e) => change('company', e.target.value)}
            error={!!errors.company}
            helperText={errors.company}
            fullWidth
            required
          />
          <TextField
            label="Role"
            value={form.jobTitle}
            onChange={(e) => change('jobTitle', e.target.value)}
            error={!!errors.jobTitle}
            helperText={errors.jobTitle || 'The seat you believe is opening, e.g. CFO'}
            fullWidth
            required
          />
        </FieldPair>
      </FormSection>

      <FormSection title="The signal">
          <FormControl fullWidth>
            <InputLabel id="signal-type-label">Signal type</InputLabel>
            <Select
              labelId="signal-type-label"
              value={form.signalType}
              label="Signal type"
              onChange={(e) => change('signalType', e.target.value)}
            >
              {SIGNAL_TYPES.map((t) => (
                <MenuItem key={t} value={t}>
                  {SIGNAL_TYPE_LABELS[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="What happened"
            value={form.signalText}
            onChange={(e) => change('signalText', e.target.value)}
            error={!!errors.signalText}
            helperText={errors.signalText}
            multiline
            rows={3}
            fullWidth
            required
          />
          <TextField
            label="Who is leaving the seat"
            value={form.vacatedBy}
            onChange={(e) => change('vacatedBy', e.target.value)}
            fullWidth
            helperText="Optional. The first person to reach out to when there is no posting."
          />
      </FormSection>

      <FormSection title="Where it came from">
        <FieldPair>
          <TextField
            label="Observed"
            type="date"
            value={form.signalObservedDate}
            onChange={(e) => change('signalObservedDate', e.target.value)}
            error={!!errors.signalObservedDate}
            helperText={errors.signalObservedDate}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Source"
            value={form.signalSource}
            onChange={(e) => change('signalSource', e.target.value)}
            fullWidth
            helperText="Optional, e.g. LinkedIn, company blog, press release"
          />
        </FieldPair>
      </FormSection>
    </DialogShell>
  )
}
